import React, { useCallback, useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import {
  Check, ChevronRight, CreditCard, Eye, EyeOff, Loader2,
  RefreshCw, Smartphone, Store, TestTube, Wifi,
} from 'lucide-react';

const MAGASINS = [
  { id: 'M1', hintKey: 'sumupTerminal.hintM1' },
  { id: 'M2', hintKey: 'sumupTerminal.hintM2' },
];

const EMPTY = {
  api_key: '',
  merchant_code: '',
  reader_id: '',
  device_id: '',
  label: '',
  enabled: true,
};

const SumUpTerminalConfigPage = () => {
  const { api } = useAuth();
  const { t, formatCurrency } = useLanguage();
  const [activeMag, setActiveMag] = useState('M1');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [form, setForm] = useState({ ...EMPTY });
  const [saved, setSaved] = useState(null);
  const [showKey, setShowKey] = useState(false);
  const [readers, setReaders] = useState([]);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [keyVerified, setKeyVerified] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/sumup-terminal/config');
      const row = (res.data?.magasins || []).find((m) => m.magasin_id === activeMag);
      setSaved(row || null);
      setForm({
        api_key: '',
        merchant_code: row?.merchant_code || '',
        reader_id: row?.reader_id || '',
        device_id: row?.device_id || '',
        label: row?.label || '',
        enabled: row?.configured ? row?.enabled !== false : true,
      });
      setKeyVerified(false);
    } catch (e) {
      setError(e.response?.data?.detail || t('sumupTerminal.errLoad'));
    } finally {
      setLoading(false);
    }
  }, [api, activeMag, t]);

  useEffect(() => { load(); }, [load]);

  const patch = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const onMerchantChange = (value) => {
    const changed = saved?.merchant_code && value.trim().toUpperCase() !== saved.merchant_code.toUpperCase();
    setForm((prev) => ({
      ...prev,
      merchant_code: value,
      ...(changed ? { reader_id: '', device_id: '', api_key: prev.api_key } : {}),
    }));
    setKeyVerified(false);
    if (changed) {
      setReaders([]);
      setMessage(t('sumupTerminal.msgMerchantChanged'));
    }
  };

  const onApiKeyChange = (value) => {
    patch('api_key', value);
    setKeyVerified(false);
    if (value.trim()) {
      setForm((prev) => ({ ...prev, reader_id: '', device_id: '' }));
      setReaders([]);
    }
  };

  const handleVerifyKey = async () => {
    if (!form.api_key.trim()) {
      setError(t('sumupTerminal.errPasteSecret'));
      return;
    }
    setVerifying(true);
    setError('');
    setMessage('');
    setReaders([]);
    try {
      const res = await api.post('/sumup-terminal/verify-key', {
        api_key: form.api_key,
        merchant_code: form.merchant_code || undefined,
      });
      const mc = res.data?.merchant_code || form.merchant_code;
      const list = res.data?.readers || [];
      setForm((prev) => ({
        ...prev,
        merchant_code: mc,
        reader_id: list.length === 1 ? list[0].id : prev.reader_id,
        device_id: list.length === 1 ? (list[0].device_id || '') : prev.device_id,
      }));
      setReaders(list);
      setKeyVerified(true);
      setMessage(
        list.length
          ? t('sumupTerminal.keyOkReaders', { mc, count: list.length })
          : t('sumupTerminal.keyOkNone', { mc })
      );
    } catch (e) {
      setKeyVerified(false);
      setError(e.response?.data?.detail || t('sumupTerminal.errKeyRejected'));
    } finally {
      setVerifying(false);
    }
  };

  const handleDiscover = async () => {
    setDiscovering(true);
    setError('');
    setReaders([]);
    try {
      const res = await api.post('/sumup-terminal/discover-readers', {
        magasin_id: activeMag,
        api_key: form.api_key || undefined,
        merchant_code: form.merchant_code,
      });
      if (res.data?.merchant_code) {
        patch('merchant_code', res.data.merchant_code);
      }
      setReaders(res.data?.readers || []);
      if (!res.data?.readers?.length) {
        setMessage(t('sumupTerminal.msgNoReaders'));
      }
    } catch (e) {
      setError(e.response?.data?.detail || t('sumupTerminal.errDiscover'));
    } finally {
      setDiscovering(false);
    }
  };

  const pickReader = (r) => {
    patch('reader_id', r.id);
    patch('device_id', r.device_id || form.device_id);
    if (!form.label) patch('label', r.name || '');
    setMessage(t('sumupTerminal.readerSelected', { name: r.name || r.id }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setMessage('');
    const merchantChanged = saved?.merchant_code
      && form.merchant_code
      && form.merchant_code.trim().toUpperCase() !== saved.merchant_code.toUpperCase();
    if (merchantChanged && !form.api_key.trim()) {
      setError(t('sumupTerminal.errMerchantChangeKey'));
      setSaving(false);
      return;
    }
    if (form.api_key.trim() && !keyVerified) {
      setError(t('sumupTerminal.errVerifyFirst'));
      setSaving(false);
      return;
    }
    if (!form.api_key.trim() && merchantChanged) {
      setSaving(false);
      return;
    }
    if (!form.reader_id.trim()) {
      setError(t('sumupTerminal.errReaderRequired'));
      setSaving(false);
      return;
    }
    try {
      const res = await api.put(`/sumup-terminal/config/${activeMag}`, { ...form, enabled: true });
      const notice = res.data?.notice;
      setMessage(
        notice
          ? t('sumupTerminal.savedWithNotice', { mag: activeMag, notice })
          : t('sumupTerminal.saved', { mag: activeMag })
      );
      setForm((prev) => ({
        ...prev,
        api_key: '',
        merchant_code: res.data?.merchant_code || prev.merchant_code,
        reader_id: res.data?.reader_id || prev.reader_id,
        device_id: res.data?.device_id || prev.device_id,
        label: res.data?.label || prev.label,
      }));
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || t('sumupTerminal.errSave'));
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setError('');
    setMessage('');
    try {
      const res = await api.post(`/sumup-terminal/config/${activeMag}/test`);
      setMessage(res.data?.message || t('sumupTerminal.testOk'));
    } catch (e) {
      setError(e.response?.data?.detail || t('sumupTerminal.errTest'));
    } finally {
      setTesting(false);
    }
  };

  const magInfo = MAGASINS.find((m) => m.id === activeMag);

  return (
    <Layout>
      <div className="max-w-2xl mx-auto fade-in space-y-5" data-testid="sumup-terminal-config-page">
        <div>
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-blue-400" />
            {t('sumupTerminal.title')}
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            {t('sumupTerminal.subtitle')}
          </p>
        </div>

        <div className="flex gap-2">
          {MAGASINS.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => setActiveMag(m.id)}
              className={`flex-1 py-3 px-4 rounded-xl border text-left transition-all ${
                activeMag === m.id
                  ? 'border-blue-500/50 bg-blue-950/30 text-white'
                  : 'border-white/10 bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
              data-testid={`sumup-tab-${m.id}`}
            >
              <div className="font-semibold text-sm flex items-center gap-1.5">
                <Store className="w-4 h-4" />
                {m.id}
                {saved?.configured && activeMag === m.id && (
                  <Check className="w-3.5 h-3.5 text-emerald-400 ml-auto" />
                )}
              </div>
              <div className="text-[10px] mt-0.5 opacity-70">{t(m.hintKey)}</div>
            </button>
          ))}
        </div>

        {activeMag === 'M2' && !saved?.configured && (
          <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/30 text-sm text-amber-100">
            {t('sumupTerminal.m2NotConfigured')}
          </div>
        )}

        <ol className="space-y-1 text-xs text-gray-500 list-decimal list-inside">
          <li>
            {t('sumupTerminal.step1Lead')}{' '}
            <strong className="text-gray-300">{t('sumupTerminal.step1Merchant')}</strong>
            {t('sumupTerminal.step1And')}
            <strong className="text-gray-300">{t('sumupTerminal.step1Reader')}</strong>
          </li>
          <li>
            {t('sumupTerminal.step2Lead')}{' '}
            <strong className="text-gray-300">{t('sumupTerminal.step2Secret')}</strong>{' '}
            {t('sumupTerminal.step2Mid')}{' '}
            <strong className="text-red-300">{t('sumupTerminal.step2Public')}</strong>
          </li>
          <li>
            {t('sumupTerminal.step3Lead')}{' '}
            <strong className="text-gray-300">{t('sumupTerminal.step3Verify')}</strong>{' '}
            {t('sumupTerminal.step3Rest')}
          </li>
          <li>
            {t('sumupTerminal.step4Lead')}{' '}
            <strong className="text-gray-300">{t('sumupTerminal.step4Save')}</strong>
          </li>
        </ol>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gray-500" /></div>
        ) : (
          <div className="p-4 rounded-xl border border-white/10 bg-[#151820] space-y-4">
            <div>
              <label className="block text-xs text-gray-400 mb-1">{t('sumupTerminal.labelName')}</label>
              <input
                className="input-dark w-full py-2.5"
                value={form.label}
                onChange={(e) => patch('label', e.target.value)}
                placeholder={t('sumupTerminal.namePlaceholder')}
                data-testid="sumup-label"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">{t('sumupTerminal.labelApiKey')}</label>
              <div className="relative">
                <input
                  type={showKey ? 'text' : 'password'}
                  className="input-dark w-full py-2.5 font-mono text-sm pr-10"
                  value={form.api_key}
                  onChange={(e) => onApiKeyChange(e.target.value)}
                  placeholder={saved?.api_key_masked ? t('sumupTerminal.savedKeyPlaceholder', { masked: saved.api_key_masked }) : 'sup_sk_…'}
                  data-testid="sumup-api-key"
                />
                <button type="button" onClick={() => setShowKey((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                  {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-[10px] text-gray-600 mt-1">
                {t('sumupTerminal.apiKeyHint')}
              </p>
              <button
                type="button"
                onClick={handleVerifyKey}
                disabled={verifying || !form.api_key.trim()}
                className="mt-2 w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-blue-600/80 hover:bg-blue-600 text-white text-sm font-semibold disabled:opacity-40"
                data-testid="sumup-verify-key"
              >
                {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                {t('sumupTerminal.verifyKeyBtn')}
              </button>
              {keyVerified && (
                <p className="text-[10px] text-emerald-400 mt-1">{t('sumupTerminal.keyConfirmed')}</p>
              )}
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">{t('sumupTerminal.labelMerchantCode')}</label>
              <input
                className="input-dark w-full py-2.5 font-mono"
                value={form.merchant_code}
                onChange={(e) => onMerchantChange(e.target.value)}
                placeholder="MCFK65MW"
                data-testid="sumup-merchant"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">{t('sumupTerminal.labelReaderId')}</label>
              <div className="flex gap-2">
                <input
                  className="input-dark flex-1 py-2.5 font-mono text-sm"
                  value={form.reader_id}
                  onChange={(e) => patch('reader_id', e.target.value)}
                  placeholder="rdr_…"
                  data-testid="sumup-reader-id"
                />
                <button
                  type="button"
                  onClick={handleDiscover}
                  disabled={discovering || !form.merchant_code}
                  className="shrink-0 px-3 py-2 rounded-lg bg-white/10 text-xs font-semibold text-gray-200 flex items-center gap-1"
                  data-testid="sumup-discover"
                >
                  {discovering ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                  {t('sumupTerminal.discoverBtn')}
                </button>
              </div>
            </div>
            {readers.length > 0 && (
              <div className="space-y-2">
                {readers.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => pickReader(r)}
                    className="w-full text-left p-3 rounded-lg border border-white/10 hover:border-blue-500/40 bg-black/20"
                  >
                    <div className="text-sm font-semibold text-white">{r.name || t('sumupTerminal.readerFallback')}</div>
                    <div className="text-[10px] text-gray-500 font-mono mt-1">
                      {r.id} · {r.status} · {r.device_id || '—'}
                    </div>
                  </button>
                ))}
              </div>
            )}
            <div>
              <label className="block text-xs text-gray-400 mb-1">{t('sumupTerminal.labelDeviceId')}</label>
              <input
                className="input-dark w-full py-2.5 font-mono text-sm"
                value={form.device_id}
                onChange={(e) => patch('device_id', e.target.value)}
                placeholder="200101270220"
                data-testid="sumup-device-id"
              />
            </div>

            {error && (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-xs text-red-300">{error}</div>
            )}
            {message && (
              <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-xs text-emerald-200">{message}</div>
            )}

            <div className="flex flex-wrap gap-2 pt-2">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-red-600 hover:bg-red-500 text-white text-sm font-semibold disabled:opacity-50"
                data-testid="sumup-save"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                {t('sumupTerminal.saveBtn', { mag: activeMag })}
              </button>
              <button
                type="button"
                onClick={handleTest}
                disabled={testing}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-white/15 text-sm text-gray-200 disabled:opacity-50"
                data-testid="sumup-test"
              >
                {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <TestTube className="w-4 h-4" />}
                {t('sumupTerminal.testBtn', { amount: formatCurrency(0.01) })}
              </button>
            </div>
          </div>
        )}

        <div className="p-4 rounded-xl bg-white/5 border border-white/10 text-xs text-gray-500 space-y-2">
          <p className="flex items-center gap-1.5 text-gray-400 font-semibold">
            <Wifi className="w-3.5 h-3.5" /> {t('sumupTerminal.checklistTitle')}
          </p>
          <ul className="space-y-1">
            <li>{t('sumupTerminal.checklistWifi')}</li>
            <li>{t('sumupTerminal.checklistPaired')}</li>
            <li>{t('sumupTerminal.checklistSolo')}</li>
            <li>{t('sumupTerminal.checklistBluetooth')}</li>
            <li>{t('sumupTerminal.checklistCancel')}</li>
          </ul>
          <a
            href="https://me.sumup.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-blue-400 hover:underline mt-2"
          >
            {t('sumupTerminal.openCloud')} <ChevronRight className="w-3 h-3" />
          </a>
        </div>
      </div>
    </Layout>
  );
};

export default SumUpTerminalConfigPage;
